import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as billApi from '../../../../../services/bills.services';

export const useBillData = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [billData, setBillData] = useState(null);
  const [originalValues, setOriginalValues] = useState({
    advancePaid: 0,
    balance: 0,
    govtTax: 0,
    perHead: 0,
    totalAmount: 0
  });
  
const [formData, setFormData] = useState({
  eventDetails: {
    name: '',
    cnic: '',
    dateOfEvent: '',
    dayOfEvent: '',
    venue: '',
    advancePayment: '',
    lunchDinner: '',
    persons: '',
    mob1: '',
    mob2: ''
  },
  billDetails: {
    foodBill: '',
    djMusic: '',
    serviceCharges: '',
    hallRent: '',
    bwdStage: '',
    acCharges: '',
    heaterCharges: '',
    ladiesWaiter: '',
    govtTax: '',
    otherCharges: '',
    discount: '',
    advancePaid: '',
    receivedPayment: '',
    totalAmount: '',
    remainingAmount: '',
    includeTax: false // Add this line
  },
  menu: [],
  perHead: '',
  serviceType: '',
  status: 'pending'
});

  useEffect(() => {
    fetchBillData();
  }, [id]);

  const fetchBillData = async () => {
    try {
      setLoading(true);
      const response = await billApi.getBillById(id);
      
      if (response.data && response.data.success) {
        const bill = response.data.data;
        setBillData(bill);
        
        const advancePaymentFromEvent = bill.eventDetails?.advancePayment || 0;
        const remainingAmount = bill.billDetails?.remainingAmount || 0;
        const govtTaxFromBill = bill.billDetails?.govtTax || 0;
        const perHeadFromBill = bill.perHead || 0;
        const totalAmountFromBill = bill.billDetails?.totalAmount || 0;
        
        setOriginalValues({
          advancePaid: advancePaymentFromEvent,
          balance: remainingAmount,
          govtTax: govtTaxFromBill,
          perHead: perHeadFromBill,
          totalAmount: totalAmountFromBill
        });
        
        // Calculate per head if not exists
        let perHead = perHeadFromBill;
        if (!perHead && bill.eventDetails?.persons && bill.billDetails?.foodBill) {
          perHead = Math.round(bill.billDetails.foodBill / bill.eventDetails.persons);
        }
        
        // Calculate menu item prices
        const menuItems = bill.menu?.map(item => ({
          item: item.item || '',
          price: item.price || 0,
          _id: item._id
        })) || [];
        
        setFormData({
          eventDetails: {
            name: bill.eventDetails?.name || '',
            cnic: bill.eventDetails?.cnic || '',
            dateOfEvent: bill.eventDetails?.dateOfEvent ? 
              bill.eventDetails.dateOfEvent.split('T')[0] : '',
            dayOfEvent: bill.eventDetails?.dayOfEvent || '',
            venue: bill.eventDetails?.venue || '',
            advancePayment: bill.eventDetails?.advancePayment || '',
            lunchDinner: bill.eventDetails?.lunchDinner || '',
            persons: bill.eventDetails?.persons || '',
            mob1: bill.eventDetails?.mob1 || '',
            mob2: bill.eventDetails?.mob2 || ''
          },
          billDetails: {
            foodBill: bill.billDetails?.foodBill || '',
            djMusic: bill.billDetails?.djMusic || '',
            serviceCharges: bill.billDetails?.serviceCharges || '',
            hallRent: bill.billDetails?.hallRent || '',
            bwdStage: bill.billDetails?.bwdStage || '',
            acCharges: bill.billDetails?.acCharges || '',
            heaterCharges: bill.billDetails?.heaterCharges || '',
            ladiesWaiter: bill.billDetails?.ladiesWaiter || '',
            govtTax: govtTaxFromBill.toString(),
            otherCharges: bill.billDetails?.otherCharges || '',
            discount: bill.billDetails?.discount || '',
            advancePaid: advancePaymentFromEvent.toString(),
            receivedPayment: '',
            totalAmount: bill.billDetails?.totalAmount || '',
            remainingAmount: remainingAmount.toString()
          },
          menu: menuItems,
          perHead: perHead.toString(),
          serviceType: bill.serviceType || '',
          status: bill.status || 'pending'
        });
      }
    } catch (err) {
      setError('Failed to load bill data. Please try again.');
      toast.error('Failed to load bill data');
    } finally {
      setLoading(false);
    }
  };

  const recalculateFoodBill = (data) => {
    const perHead = parseFloat(data.perHead) || 0;
    const persons = parseFloat(data.eventDetails.persons) || 0;
    const menuTotal = data.menu.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    
    const baseFoodBill = perHead * persons;
    const foodBill = baseFoodBill + menuTotal;
    
    data.billDetails.foodBill = foodBill.toString();
    
    // Recalculate the entire bill
    recalculateBill(data);
    
    return data;
  };

const recalculateBill = (data) => {
  // Parse all values
  const foodBill = parseFloat(data.billDetails.foodBill) || 0;
  const djMusic = parseFloat(data.billDetails.djMusic) || 0;
  const serviceCharges = parseFloat(data.billDetails.serviceCharges) || 0;
  const hallRent = parseFloat(data.billDetails.hallRent) || 0;
  const bwdStage = parseFloat(data.billDetails.bwdStage) || 0;
  const acCharges = parseFloat(data.billDetails.acCharges) || 0;
  const heaterCharges = parseFloat(data.billDetails.heaterCharges) || 0;
  const ladiesWaiter = parseFloat(data.billDetails.ladiesWaiter) || 0;
  const otherCharges = parseFloat(data.billDetails.otherCharges) || 0;
  
  // Calculate subtotal
  const subtotal = foodBill + djMusic + serviceCharges + hallRent + bwdStage + 
                  acCharges + heaterCharges + ladiesWaiter + otherCharges;
  
  // Government tax
  const govtTaxAmount = parseFloat(data.billDetails.govtTax) || 0;
  const includeTax = data.billDetails.includeTax || false;
  
  // Calculate after tax (only if tax is included)
  const afterTax = includeTax ? subtotal + govtTaxAmount : subtotal;
  
  // Discount
  const discountAmount = parseFloat(data.billDetails.discount) || 0;
  
  // Calculate new total (tax only added if included)
  const newTotalAmount = afterTax - discountAmount;
  data.billDetails.totalAmount = newTotalAmount.toString();
  
  // Calculate advance paid (original + received)
  const originalAdvance = originalValues.advancePaid;
  const receivedPayment = parseFloat(data.billDetails.receivedPayment) || 0;
  const newAdvancePaid = originalAdvance + receivedPayment;
  data.billDetails.advancePaid = newAdvancePaid.toString();
  
  // Calculate remaining amount (total - advance paid)
  // Tax is already handled in totalAmount calculation
  const newRemainingAmount = newTotalAmount - newAdvancePaid;
  data.billDetails.remainingAmount = newRemainingAmount.toString();
  
  return data;
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      
      const newFormData = {
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      };
      
      // Recalculate if needed
      if (parent === 'billDetails') {
        recalculateBill(newFormData);
      } else if (parent === 'eventDetails' && child === 'persons') {
        recalculateFoodBill(newFormData);
      }
      
      setFormData(newFormData);
    } else if (name === 'perHead') {
      const newFormData = {
        ...formData,
        perHead: value
      };
      recalculateFoodBill(newFormData);
      setFormData(newFormData);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const addMenuItem = (item) => {
    const newItem = {
      item: item.item,
      price: parseFloat(item.price) || 0,
      _id: `temp_${Date.now()}`
    };
    
    const updatedMenu = [...formData.menu, newItem];
    const newFormData = {
      ...formData,
      menu: updatedMenu
    };
    
    recalculateFoodBill(newFormData);
    setFormData(newFormData);
    toast.success('Menu item added successfully');
  };

  const removeMenuItem = (index) => {
    const updatedMenu = [...formData.menu];
    updatedMenu.splice(index, 1);
    
    const newFormData = {
      ...formData,
      menu: updatedMenu
    };
    
    recalculateFoodBill(newFormData);
    setFormData(newFormData);
    toast.success('Menu item removed');
  };

  const updateMenuItem = (index, field, value) => {
    const updatedMenu = [...formData.menu];
    
    if (field === 'price') {
      updatedMenu[index][field] = parseFloat(value) || 0;
    } else {
      updatedMenu[index][field] = value;
    }
    
    const newFormData = {
      ...formData,
      menu: updatedMenu
    };
    
    recalculateFoodBill(newFormData);
    setFormData(newFormData);
  };

  const validateForm = () => {
    if (!formData.eventDetails.name.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    
    if (!formData.eventDetails.persons || parseFloat(formData.eventDetails.persons) <= 0) {
      toast.error('Number of persons must be greater than 0');
      return false;
    }
    
    const receivedPayment = parseFloat(formData.billDetails.receivedPayment) || 0;
    if (receivedPayment < 0) {
      toast.error('Received payment cannot be negative');
      return false;
    }
    
    const totalAmount = parseFloat(formData.billDetails.totalAmount) || 0;
    const advancePaid = parseFloat(formData.billDetails.advancePaid) || 0;
    
    if (receivedPayment > (totalAmount - (advancePaid - receivedPayment))) {
      toast.error('Received payment cannot exceed remaining amount');
      return false;
    }
    
    return true;
  };

const handleSubmit = async () => {
  try {
    setSaving(true);
    setError('');
    setSuccess('');
    
    const loadingToast = toast.loading('Updating bill...');
    
    // Use current form data for calculations
    const receivedPayment = parseFloat(formData.billDetails.receivedPayment) || 0;
    const originalAdvance = originalValues.advancePaid;
    const newAdvancePayment = originalAdvance + receivedPayment;
    
    // Calculate total from current form data
    const foodBill = parseFloat(formData.billDetails.foodBill) || 0;
    const djMusic = parseFloat(formData.billDetails.djMusic) || 0;
    const serviceCharges = parseFloat(formData.billDetails.serviceCharges) || 0;
    const hallRent = parseFloat(formData.billDetails.hallRent) || 0;
    const bwdStage = parseFloat(formData.billDetails.bwdStage) || 0;
    const acCharges = parseFloat(formData.billDetails.acCharges) || 0;
    const heaterCharges = parseFloat(formData.billDetails.heaterCharges) || 0;
    const ladiesWaiter = parseFloat(formData.billDetails.ladiesWaiter) || 0;
    const otherCharges = parseFloat(formData.billDetails.otherCharges) || 0;
    const govtTax = parseFloat(formData.billDetails.govtTax) || 0;
    const discount = parseFloat(formData.billDetails.discount) || 0;
    const includeTax = formData.billDetails.includeTax || false;
    
    const subtotal = foodBill + djMusic + serviceCharges + hallRent + bwdStage + 
                    acCharges + heaterCharges + ladiesWaiter + otherCharges;
    
    // Only add tax if included
    const afterTax = includeTax ? subtotal + govtTax : subtotal;
    const newTotalAmount = afterTax - discount;
    const newRemainingAmount = newTotalAmount - newAdvancePayment;
    
    const updateData = {
      eventDetails: {
        name: formData.eventDetails.name,
        cnic: formData.eventDetails.cnic,
        dateOfEvent: formData.eventDetails.dateOfEvent,
        dayOfEvent: formData.eventDetails.dayOfEvent,
        venue: formData.eventDetails.venue,
        advancePayment: newAdvancePayment,
        lunchDinner: formData.eventDetails.lunchDinner,
        persons: parseInt(formData.eventDetails.persons) || 0,
        mob1: formData.eventDetails.mob1,
        mob2: formData.eventDetails.mob2
      },
      billDetails: {
        foodBill: foodBill,
        djMusic: djMusic,
        serviceCharges: serviceCharges,
        hallRent: hallRent,
        bwdStage: bwdStage,
        acCharges: acCharges,
        heaterCharges: heaterCharges,
        ladiesWaiter: ladiesWaiter,
        govtTax: govtTax,
        otherCharges: otherCharges,
        discount: discount,
        totalAmount: newTotalAmount,
        remainingAmount: newRemainingAmount,
        includeTax: includeTax // Save the tax preference
      },
      menu: formData.menu.map(item => ({
        item: item.item,
        price: item.price
      })),
      perHead: parseFloat(formData.perHead) || 0,
      serviceType: formData.serviceType,
      status: formData.status,
      receivedPayment: receivedPayment
    };
    
    console.log('Updating bill with data:', updateData);
    
    await billApi.updateBill(id, updateData);
    
    toast.dismiss(loadingToast);
    toast.success('✅ Bill updated successfully!');
    
    setSuccess('Bill updated successfully!');
    
    setTimeout(() => {
      navigate('/admin/dashboard/bills-list');
    }, 1500);
    
  } catch (err) {
    toast.dismiss();
    const errorMessage = err.response?.data?.message || err.message || 'Failed to update bill';
    setError(errorMessage);
    toast.error(`❌ ${errorMessage}`);
  } finally {
    setSaving(false);
  }
};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const calculateFoodBillBreakdown = () => {
    const perHead = parseFloat(formData.perHead) || 0;
    const persons = parseFloat(formData.eventDetails.persons) || 0;
    const menuTotal = formData.menu.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    const baseFoodBill = perHead * persons;
    const totalFoodBill = baseFoodBill + menuTotal;
    
    return {
      perHead,
      persons,
      menuTotal,
      baseFoodBill,
      totalFoodBill
    };
  };

  return {
    id,
    navigate,
    loading,
    saving,
    error,
    success,
    billData,
    originalValues,
    formData,
    setError,
    setSuccess,
    handleInputChange,
    addMenuItem,
    removeMenuItem,
    updateMenuItem,
    validateForm,
    handleSubmit,
    formatCurrency,
    calculateFoodBillBreakdown
  };
};